import React, { useState, useEffect } from "react"
import Icon from "../AppIcon"
import Button from "../ui/Button"
import AppointmentScheduler from "./AppointmentScheduler"
import {
  subscribeAppointments,
  respondToAppointment,
  cancelAppointment,
} from "../../utils/mentorshipHelpers"

function formatDateTime(ms) {
  if (!ms) return ""
  const d = new Date(ms)
  const dateStr = d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })
  let hours = d.getHours()
  const minutes = d.getMinutes()
  const ampm = hours >= 12 ? "PM" : "AM"
  hours = hours % 12
  if (hours === 0) hours = 12
  const minutesStr = minutes < 10 ? `0${minutes}` : `${minutes}`
  return `${dateStr} · ${hours}:${minutesStr} ${ampm}`
}

// currentUser: { uid, name }
// canPropose: only the mentor side should see the "Propose a Time" trigger,
// per the mentor-proposes / mentee-confirms flow.
const MentorshipAppointments = ({ orgId, mentorshipId, currentUser, otherName, canPropose }) => {
  const [appointments, setAppointments] = useState([])
  const [schedulerOpen, setSchedulerOpen] = useState(false)

  useEffect(() => {
    if (!orgId || !mentorshipId) return
    return subscribeAppointments(orgId, mentorshipId, setAppointments)
  }, [orgId, mentorshipId])

  const pending = appointments.filter((a) => a.status === "proposed")
  const upcoming = appointments
    .filter((a) => a.status === "confirmed" && a.scheduledForMs >= Date.now())
    .sort((a, b) => a.scheduledForMs - b.scheduledForMs)

  const handleRespond = async (a, status) => {
    await respondToAppointment(orgId, mentorshipId, a.id, { uid: currentUser.uid, status })
  }

  const handleCancel = async (a) => {
    if (!window.confirm("Cancel this appointment?")) return
    await cancelAppointment(orgId, mentorshipId, a.id, currentUser.uid)
  }

  return (
    <div className="space-y-4">
      {canPropose && (
        <>
          {!schedulerOpen ? (
            <Button variant="default" size="sm" iconName="CalendarPlus" iconPosition="left" onClick={() => setSchedulerOpen(true)}>
              Propose a Time
            </Button>
          ) : (
            <AppointmentScheduler
              orgId={orgId}
              mentorshipId={mentorshipId}
              currentUser={currentUser}
              existingAppointments={appointments}
              onClose={() => setSchedulerOpen(false)}
              onProposed={() => setSchedulerOpen(false)}
            />
          )}
        </>
      )}

      {pending.length > 0 && (
        <div className="bg-card border border-border rounded-xl p-5 space-y-3">
          <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">Pending</h3>
          {pending.map((a) => {
            const isMine = a.proposedByUid === currentUser.uid
            return (
              <div key={a.id} className="flex items-center justify-between py-2 border-b border-border last:border-0 gap-3">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-gray-900">{formatDateTime(a.scheduledForMs)}</p>
                  {a.note && <p className="text-xs text-muted-foreground mt-0.5">{a.note}</p>}
                  {isMine && <p className="text-xs text-muted-foreground mt-0.5">Waiting for {otherName} to respond</p>}
                </div>
                {isMine ? (
                  <Button variant="ghost" size="sm" onClick={() => handleCancel(a)}>Cancel</Button>
                ) : (
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button variant="outline" size="sm" onClick={() => handleRespond(a, "declined")}>Decline</Button>
                    <Button variant="default" size="sm" onClick={() => handleRespond(a, "confirmed")}>Confirm</Button>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      <div className="bg-card border border-border rounded-xl p-5 space-y-3">
        <h3 className="text-sm font-semibold text-foreground uppercase tracking-wide">Upcoming</h3>
        {upcoming.length === 0 && (
          <p className="text-sm text-muted-foreground">No upcoming sessions scheduled.</p>
        )}
        {upcoming.map((a) => (
          <div key={a.id} className="flex items-center justify-between py-2 border-b border-border last:border-0 gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Icon name="Calendar" size={16} className="text-accent flex-shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-gray-900">{formatDateTime(a.scheduledForMs)}</p>
                {a.note && <p className="text-xs text-muted-foreground mt-0.5">{a.note}</p>}
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={() => handleCancel(a)}>Cancel</Button>
          </div>
        ))}
      </div>
    </div>
  )
}

export default MentorshipAppointments